import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';

const QuoteFormModal = ({ isOpen, onClose, productDetails }) => {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    shadeRequirement: '',
    quantity: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  // Update form when productDetails changes
  useEffect(() => {
    if (productDetails) {
      setFormData(currentFormData => ({
        ...currentFormData,
        shadeRequirement: `${productDetails.shadeName} (${productDetails.productCode})`,
        quantity: productDetails.quantity || 10
      }));
    }
  }, [productDetails]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/send-quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          productDetails: productDetails
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send quote request');
      }

      // Show success message
      setShowSuccess(true);
      
      // Reset form after 3 seconds and close modal
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          name: '',
          companyName: '',
          email: '',
          phone: '',
          shadeRequirement: '',
          quantity: '',
          message: ''
        });
        onClose();
      }, 3000);
      
    } catch (err) {
      setError('Failed to send quote request. Please try again.');
      // Error logged for debugging - consider integrating proper error tracking in production
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-3xl flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-900">Request a Quote</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="mx-8 mt-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-900 font-medium">Thank you! Your quote request has been sent.</p>
              <p className="text-green-700 text-sm mt-1">We will contact you shortly regarding the shades and quantities requested.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-900">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Name & Company Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 bg-white"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 bg-white"
                placeholder="Company name"
              />
            </div>
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 bg-white"
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 bg-white"
                placeholder="+91-9876543210"
              />
            </div>
          </div>

          {/* Shade/Color Requirement */}
          <div>
            <label htmlFor="shadeRequirement" className="block text-sm font-medium text-gray-700 mb-2">
              Shade/Color Requirement *
            </label>
            <input
              type="text"
              id="shadeRequirement"
              name="shadeRequirement"
              value={formData.shadeRequirement}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 bg-white"
              placeholder="e.g., Siemens Grey (EF-3405) or specify custom shade"
            />
          </div>

          {/* Quantity Required */}
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
              Quantity Required (KG) *
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="10"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 bg-white"
              placeholder="Minimum 10 KG"
            />
            <p className="text-sm text-gray-500 mt-2">Minimum order quantity: 10 KG</p>
          </div>

          {/* Message/Notes */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message/Notes (Optional)
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none text-gray-900 bg-white"
              placeholder="Additional requirements, delivery timeline, or any other notes..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || showSuccess}
              className={`flex-1 px-6 py-3 rounded-xl font-medium text-white transition-all ${
                isSubmitting || showSuccess
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-lg hover:scale-105'
              }`}
            >
              {(() => {
                if (isSubmitting) return 'Sending...';
                if (showSuccess) return 'Sent!';
                return 'Submit Quote Request';
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteFormModal;
